import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '../../lib/utils'

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6

const headingVariants = cva('scroll-m-20 text-foreground font-display', {
  variants: {
    level: {
      1: '',
      2: '',
      3: '',
      4: '',
      5: '',
      6: '',
    },
    size: {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
      /** Responsive hero size — page-level hero headlines */
      hero: 'text-2xl md:text-5xl',
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    /** Visual style variants orthogonal to level/size */
    variant: {
      default: '',
      /** Uppercase label style — section dividers, sidebar group headings */
      overline: 'uppercase tracking-wider text-muted-foreground',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const LEVEL_TAG_MAP: Record<HeadingLevel, 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'> = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
}

const DEFAULT_SIZE_BY_LEVEL: Record<HeadingLevel, NonNullable<VariantProps<typeof headingVariants>['size']>> = {
  1: '2xl',
  2: 'xl',
  3: 'lg',
  4: 'base',
  5: 'sm',
  6: 'xs',
}

/** Weight defaults — explicit `weight` prop overrides. */
const DEFAULT_WEIGHT_BY_LEVEL: Record<HeadingLevel, NonNullable<VariantProps<typeof headingVariants>['weight']>> = {
  1: 'normal',
  2: 'normal',
  3: 'normal',
  4: 'normal',
  5: 'normal',
  6: 'normal',
}

export interface HeadingProps
  extends React.HTMLAttributes<HTMLHeadingElement>,
  Omit<VariantProps<typeof headingVariants>, 'level'> {
  level?: HeadingLevel
}

export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, level = 1, size, weight, variant, ...props }, ref) => {
    const Comp = LEVEL_TAG_MAP[level]
    const resolvedSize = size ?? DEFAULT_SIZE_BY_LEVEL[level]
    const resolvedWeight = weight ?? DEFAULT_WEIGHT_BY_LEVEL[level]

    return (
      <Comp
        ref={ref}
        className={cn(headingVariants({ level, size: resolvedSize, weight: resolvedWeight, variant }), className)}
        {...props}
      />
    )
  },
)

Heading.displayName = 'Heading'
